import React from 'react';

import { applyThemeMode, useThemeMode } from './theme';
export { applyThemeMode, useThemeMode } from './theme';
export type { ThemeMode } from './theme';

const THEME_OPTIONS = [
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
];

type ClassDictionary = Record<string, boolean | null | undefined>;
type ClassValue = string | number | false | null | undefined | ClassDictionary | ClassValue[];
type ChangeHandler = (value: string) => void;

const flattenClassValue = (value: ClassValue): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(flattenClassValue);
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([className]) => className);
  }
  return [String(value)];
};

export function cn(...classes: ClassValue[]): string {
  return classes.flatMap(flattenClassValue).filter(Boolean).join(' ');
}

interface BaseProps {
  className?: string;
  children?: React.ReactNode;
}

interface ThemeToggleProps extends BaseProps {
  label?: string;
}

export function ThemeToggle({ className, label = 'Theme' }: ThemeToggleProps): React.ReactElement {
  const [mode, setMode] = useThemeMode();

  return (
    <SegmentedControl
      className={cn('bsui-theme-toggle', className)}
      label={label}
      options={THEME_OPTIONS}
      value={mode}
      onChange={setMode}
    />
  );
}

type ButtonOwnProps = BaseProps & {
  as?: React.ElementType;
  variant?:
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'ghost'
    | 'outline'
    | 'outline-primary'
    | 'outline-danger'
    | 'success'
    | 'warning'
    | 'link';
  size?: 'sm' | 'md' | 'lg' | string;
  fullWidth?: boolean;
  loading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconAfter?: React.ReactNode;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
};

type ButtonProps = ButtonOwnProps
  & Omit<React.HTMLAttributes<HTMLElement>, keyof ButtonOwnProps>
  & {
    href?: string;
    to?: string;
    target?: string;
    rel?: string;
  };

export const Button = React.forwardRef<HTMLElement, ButtonProps>(function Button(
  {
    as,
    className,
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    disabled,
    icon,
    iconAfter,
    children,
    type,
    onClick,
    href,
    ...props
  },
  ref,
) {
  const Component = as || (href ? 'a' : 'button');
  const isButton = Component === 'button';
  const RenderComponent = Component as React.ElementType;
  const isDisabled = Boolean(disabled || loading);
  const routeTarget = (props as { to?: unknown }).to;
  const anchorHref = href || (typeof routeTarget === 'string' ? routeTarget : undefined);
  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (!isButton && isDisabled) {
      event.preventDefault();
      return;
    }

    onClick?.(event);
  };

  return (
    <RenderComponent
      ref={ref}
      className={cn(
        'bsui-button',
        `bsui-button--${variant}`,
        size !== 'md' && `bsui-button--${size}`,
        fullWidth && 'bsui-button--full',
        className,
      )}
      disabled={isButton ? isDisabled : undefined}
      aria-disabled={!isButton && isDisabled ? true : undefined}
      href={!isButton ? anchorHref : undefined}
      onClick={handleClick}
      type={isButton ? type || 'button' : undefined}
      {...props}
    >
      {loading ? <Spinner aria-hidden="true" /> : icon}
      {children}
      {iconAfter}
    </RenderComponent>
  );
});

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, label, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      aria-label={label}
      className={cn('bsui-icon-button', className)}
      title={label}
      type={type}
      {...props}
    >
      {children}
    </button>
  );
});

interface VariantProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: string;
}

export function Badge({ className, variant = 'neutral', children, ...props }: VariantProps): React.ReactElement {
  return (
    <span className={cn('bsui-badge', `bsui-badge--${variant}`, className)} {...props}>
      {children}
    </span>
  );
}

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: string;
}

export function Alert({ className, variant = 'info', children, ...props }: AlertProps): React.ReactElement {
  return (
    <div className={cn('bsui-alert', `bsui-alert--${variant}`, className)} role="status" {...props}>
      {children}
    </div>
  );
}

interface FieldProps extends BaseProps {
  id: string;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
}

export function Field({ id, label, hint, error, children, className }: FieldProps): React.ReactElement {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <label className={cn('bsui-field', className)} htmlFor={id}>
      {label && <span className="bsui-field__label">{label}</span>}
      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
            id,
            'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined,
            'aria-invalid': error ? true : undefined,
          })
        : children}
      {hint && <span className="bsui-field__hint" id={hintId}>{hint}</span>}
      {error && <span className="bsui-field__error" id={errorId}>{error}</span>}
    </label>
  );
}

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cn('bsui-field__control', className)} {...props} />;
});

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
function Select({ className, children, ...props }, ref) {
  return (
    <select ref={ref} className={cn('bsui-field__control', 'bsui-select', className)} {...props}>
      {children}
    </select>
  );
});

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cn('bsui-field__control', 'bsui-textarea', className)} {...props} />;
});

interface SkeletonProps extends React.HTMLAttributes<HTMLSpanElement> {
  width?: React.CSSProperties['width'];
  height?: React.CSSProperties['height'];
}

export function Skeleton({ className, width, height, style, ...props }: SkeletonProps): React.ReactElement {
  return (
    <span
      className={cn('bsui-skeleton', className)}
      style={{ width, height, ...style }}
      {...props}
    />
  );
}

interface LedgerSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  rows?: number;
}

export function LedgerSkeleton({ className, rows = 8, ...props }: LedgerSkeletonProps): React.ReactElement {
  return (
    <div className={cn('bsui-ledger-skeleton', className)} aria-busy="true" {...props}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div className="bsui-ledger-skeleton__row" key={rowIndex}>
          <div className="bsui-ledger-skeleton__main">
            <Skeleton height={18} width="min(230px, 72%)" />
            <Skeleton height={14} width={82} />
          </div>
          <div className="bsui-ledger-skeleton__proof">
            <Skeleton height={14} width={82} />
            <Skeleton height={14} width={82} />
            <Skeleton height={14} width={82} />
          </div>
          <Skeleton height={40} width={88} />
        </div>
      ))}
    </div>
  );
}

interface SpinnerProps extends React.HTMLAttributes<HTMLSpanElement> {
  label?: string;
}

export function Spinner({ className, label = 'Loading', ...props }: SpinnerProps): React.ReactElement {
  return (
    <span
      className={cn('bsui-spinner', className)}
      role="status"
      aria-label={label}
      {...props}
    />
  );
}

interface EmptyStateProps extends BaseProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  icon?: React.ReactNode;
}

export function EmptyState({ className, title, description, actions, icon, children }: EmptyStateProps): React.ReactElement {
  return (
    <div className={cn('bsui-empty-state', className)}>
      {icon}
      {title && <h2>{title}</h2>}
      {description && <p>{description}</p>}
      {children}
      {actions && <div className="bsui-empty-state__actions">{actions}</div>}
    </div>
  );
}

interface PageShellProps extends React.HTMLAttributes<HTMLElement> {
  innerClassName?: string;
}

export function PageShell({ className, innerClassName, children, ...props }: PageShellProps): React.ReactElement {
  const [themeMode] = useThemeMode();
  const themeAttribute = applyThemeMode(themeMode);

  return (
    <main
      className={cn('bsui-page bsui-scope', className)}
      data-bs-theme={themeAttribute}
      {...props}
    >
      <div className={cn('bsui-page__inner', innerClassName)}>
        {children}
      </div>
    </main>
  );
}

interface PageHeaderProps extends BaseProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  supporting?: React.ReactNode;
}

export function PageHeader({ className, title, description, actions, supporting, children }: PageHeaderProps): React.ReactElement {
  return (
    <header className={cn('bsui-page-header', className)}>
      <div className="bsui-page-header__copy">
        {title && <h1>{title}</h1>}
        {description && <p className="bsui-page-header__description">{description}</p>}
        {supporting && <div className="bsui-page-header__supporting">{supporting}</div>}
        {children}
      </div>
      {actions && <div className="bsui-page-header__actions">{actions}</div>}
    </header>
  );
}

export function Toolbar({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return (
    <div className={cn('bsui-toolbar', className)} {...props}>
      {children}
    </div>
  );
}

interface SearchFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  inputClassName?: string;
  icon?: React.ReactNode;
  label?: string;
}

export const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  { className, inputClassName, icon, label = 'Search', ...props },
  ref,
) {
  return (
    <label className={cn('bsui-search-field', className)}>
      <span className="sr-only">{label}</span>
      {icon && <span className="bsui-search-field__icon" aria-hidden="true">{icon}</span>}
      <input ref={ref} className={cn('bsui-search-field__input', inputClassName)} type="search" {...props} />
    </label>
  );
});

interface DataSurfaceProps extends React.HTMLAttributes<HTMLDivElement> {
  loading?: boolean;
}

export function DataSurface({ className, children, loading = false, ...props }: DataSurfaceProps): React.ReactElement {
  return (
    <div className={cn('bsui-data-surface', loading && 'bsui-data-surface--loading', className)} {...props}>
      {children}
    </div>
  );
}

interface PanelProps extends React.HTMLAttributes<HTMLDivElement> {
  padded?: boolean;
}

export function Panel({ className, padded = true, children, ...props }: PanelProps): React.ReactElement {
  return (
    <div className={cn('bsui-panel', padded && 'bsui-panel--padded', className)} {...props}>
      {children}
    </div>
  );
}

interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  innerClassName?: string;
}

export function Section({ className, innerClassName, children, ...props }: SectionProps): React.ReactElement {
  return (
    <section className={cn('bsui-section', className)} {...props}>
      <div className={cn('bsui-section__inner', innerClassName)}>
        {children}
      </div>
    </section>
  );
}

interface SegmentedControlOption {
  value: string;
  label: React.ReactNode;
}

interface SegmentedControlProps {
  className?: string;
  options: SegmentedControlOption[];
  value: string;
  onChange: ChangeHandler;
  label?: string;
}

export function SegmentedControl({ className, options, value, onChange, label }: SegmentedControlProps): React.ReactElement {
  return (
    <div className={cn('bsui-segmented-control', className)} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="bsui-segmented-control__option"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
